import { guardArray } from "@msuite/utils/array";
import { useRef } from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { BlankSlateInline } from "../blank-slate";
import { Checkbox } from "../checkbox";
import { useFilter, useSearchForm } from "../hooks";
import { Icon } from "../icons";
import { localized } from "../localization";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";
import { TextInput } from "./text-input";

/** Props Interface */
interface InlineSelectOption {
	value: string;
	label: string;
}

interface InlineSelectProps<T extends FieldValues> extends ExtendedInputWrapperProps<T> {
	control: Control<T>;
	name: Path<T>;
	options: InlineSelectOption[];
	label: string;
}

export function InlineSelect<T extends FieldValues>({
	control,
	name,
	options,
	label,
	...props
}: InlineSelectProps<T>) {
	const searchForm = useSearchForm("inline", 0, true);
	const listRef = useRef<HTMLDivElement>(null);

	const { filtered } = useFilter(options, searchForm.searchValue);

	/** Render */
	return (
		<Controller
			control={control}
			name={name}
			render={({ field, formState }) => {
				const values = guardArray(field.value as string[]);

				function onToggle(value: string) {
					if (values.includes(value)) {
						field.onChange(values.filter((v) => v !== value));
					} else {
						field.onChange([...values, value]);
					}
				}

				function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
					if (e.key === "ArrowDown") {
						e.preventDefault();
						const first = listRef.current?.querySelector<HTMLElement>("[role='option']");
						first?.focus();
					}
				}

				function onHandleOptionKeyDown(e: React.KeyboardEvent<HTMLElement>, value: string) {
					if (e.key === " " || e.key === "Enter") {
						e.preventDefault();
						onToggle(value);
					} else if (e.key === "ArrowDown") {
						e.preventDefault();
						const next = (e.currentTarget.nextElementSibling as HTMLElement) ?? null;
						next?.focus();
					} else if (e.key === "ArrowUp") {
						e.preventDefault();
						const prev = (e.currentTarget.previousElementSibling as HTMLElement) ?? null;
						if (prev) {
							prev.focus();
						} else {
							const input = listRef.current
								?.closest("[data-inline-select]")
								?.querySelector<HTMLInputElement>("input");
							input?.focus();
						}
					}
				}

				return (
					<div data-inline-select>
						<InputWrapper
							label={label}
							errors={formState.errors}
							{...props}
							helperText={localized("misc.selected_n_out_of_m", {
								n: `${values.length}`,
								m: `${options.length}`,
							})}
						>
							<div {...searchForm.getSearchInputWrapperProps(false)}>
								<TextInput
									{...searchForm.getSearchInputProps()}
									onKeyDown={handleSearchKeyDown}
								/>
							</div>
							<div className="max-h-64 card relative p-0 overflow-hidden flex flex-col">
								<div
									ref={listRef}
									role="listbox"
									aria-multiselectable="true"
									className="overflow-y-auto *:px-3 *:py-2 divide-y-[0.5px] *:border-color min-h-0 grow-"
								>
									{!filtered.length && (
										<BlankSlateInline
											Icon={() => <Icon name="SearchX" />}
											label={localized("general.not_found")}
										/>
									)}
									{filtered.map((option) => {
										const isChecked = values.includes(option.value);

										return (
											<div
												key={option.value}
												role="option"
												aria-selected={isChecked}
												tabIndex={0}
												className="h-stack cursor-pointer focus:bg-accent-50 dark:focus:bg-accent-950/40 gap-x-3"
												onClick={() => onToggle(option.value)}
												onKeyDown={(e) => onHandleOptionKeyDown(e, option.value)}
											>
												<Checkbox
													name={option.value}
													value={isChecked}
													tabIndex={-1}
												/>
												<span className="truncate">{option.label}</span>
											</div>
										);
									})}
								</div>
							</div>
						</InputWrapper>
					</div>
				);
			}}
		/>
	);
}
